import {
	Scene,
	Vector3,
	MeshBuilder,
	StandardMaterial,
	Color3,
	TransformNode,
	Mesh,
	PointLight,
} from "@babylonjs/core";
import { AdvancedDynamicTexture, Rectangle, TextBlock, Control } from "@babylonjs/gui";

export interface InteractZone {
	id: "shop" | "kiosk";
	displayName: string;
	center: Vector3;
	radius: number;
	root: TransformNode;
	signLabel: Rectangle;
}

interface BuildingsHandles {
	zones: InteractZone[];
	adt: AdvancedDynamicTexture;
}

function makeSign(adt: AdvancedDynamicTexture, anchor: Mesh, text: string, color: string) {
	const rect = new Rectangle("sign");
	rect.width = "180px";
	rect.height = "40px";
	rect.cornerRadius = 8;
	rect.thickness = 2;
	rect.color = color;
	rect.background = "rgba(0,0,0,0.7)";
	adt.addControl(rect);
	rect.linkWithMesh(anchor);
	rect.linkOffsetY = -10;
	const t = new TextBlock();
	t.text = text;
	t.color = "#fff";
	t.fontSize = 14;
	t.fontWeight = "bold";
	t.textHorizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
	rect.addControl(t);
	return rect;
}

export function buildOutsideWorld(scene: Scene): BuildingsHandles {
	const adt = AdvancedDynamicTexture.CreateFullscreenUI("outside-labels", true, scene);
	const zones: InteractZone[] = [];

	buildRoads(scene);

	// Parts Shop — south of main road, southwest of warehouse
	zones.push(buildShop(scene, new Vector3(-16, 0, -14), adt));
	// Contracts Kiosk — south of main road, southeast of warehouse
	zones.push(buildKiosk(scene, new Vector3(14, 0, -14), adt));

	// Filler warehouses with explicit footprint sizes, placed off the road grid.
	// Road footprints:
	//   main E-W: z ∈ [-6.5, -1.5], x ∈ [-60, 60]
	//   east N-S: x ∈ [19.5, 24.5], z ∈ [-30, 40]
	//   west N-S: x ∈ [-24.5, -19.5], z ∈ [-30, 40]
	const fillers: Array<[Vector3, number, number]> = [
		[new Vector3(-36, 0, 25), 14, 14],
		[new Vector3(-36, 0, -22), 14, 14],
		[new Vector3(34, 0, 25), 14, 14],
		[new Vector3(34, 0, -22), 14, 14],
		[new Vector3(0, 0, 40), 16, 12],
	];
	for (const [pos, w, d] of fillers) buildFillerWarehouse(scene, pos, w, d);

	// Street lamps along the main east-west road on both sidewalks.
	for (let x = -42; x <= 42; x += 14) {
		buildStreetLamp(scene, new Vector3(x, 0, -7.5));
		buildStreetLamp(scene, new Vector3(x, 0, -0.5));
	}
	// Lamps along the N-S service roads.
	for (let z = -25; z <= 35; z += 14) {
		buildStreetLamp(scene, new Vector3(25.5, 0, z));
		buildStreetLamp(scene, new Vector3(-25.5, 0, z));
	}
	buildAmbientStreetLights(scene);

	// Parking spots painted on the asphalt in front of shop + kiosk.
	buildParkingSpots(scene, new Vector3(-16, 0, -9), 4);
	buildParkingSpots(scene, new Vector3(14, 0, -9), 4);

	// Trees scattered in non-road empty areas.
	for (const [x, z] of [
		[-12, 12], [12, 12], [-9, -22], [9, -22],
		[-44, 5], [44, 5], [-44, -10], [44, -10],
		[-12, 32], [12, 32],
	] as const) {
		buildTree(scene, new Vector3(x, 0, z));
	}

	// Loading dock with shipping containers north of the warehouse
	buildLoadingDock(scene, new Vector3(-8, 0, 22));
	buildLoadingDock(scene, new Vector3(8, 0, 22));

	return { zones, adt };
}

function buildRoads(scene: Scene) {
	const asphaltMat = new StandardMaterial("asphalt", scene);
	asphaltMat.diffuseColor = new Color3(0.12, 0.12, 0.13);
	asphaltMat.specularColor = new Color3(0.05, 0.05, 0.05);
	const stripeMat = new StandardMaterial("road-stripe", scene);
	stripeMat.diffuseColor = new Color3(0.95, 0.9, 0.5);
	stripeMat.emissiveColor = new Color3(0.2, 0.18, 0.05);

	const road = (name: string, x: number, z: number, w: number, d: number, rotY = 0) => {
		const m = MeshBuilder.CreateBox(name, { width: w, depth: d, height: 0.04 }, scene);
		m.position.set(x, 0.02, z);
		m.rotation.y = rotY;
		m.material = asphaltMat;
		return m;
	};

	const stripe = (name: string, x: number, z: number, w: number, d: number) => {
		const m = MeshBuilder.CreateBox(name, { width: w, depth: d, height: 0.045 }, scene);
		m.position.set(x, 0.025, z);
		m.material = stripeMat;
		return m;
	};

	// Main east-west road running south of the warehouse
	road("road-main", 0, -4, 120, 5);
	// Dashed center line — 8 short stripes east of origin, 8 west
	for (let i = -7; i <= 7; i++) {
		stripe(`stripe-main-${i}`, i * 7, -4, 2.5, 0.2);
	}

	// North-south road running between filler-east buildings (x = 28 area) and to the warehouse
	road("road-east", 22, 5, 5, 70);
	for (let i = -4; i <= 4; i++) {
		stripe(`stripe-east-${i}`, 22, 5 + i * 6, 0.2, 2.5);
	}

	// North-south road on the west side
	road("road-west", -22, 5, 5, 70);
	for (let i = -4; i <= 4; i++) {
		stripe(`stripe-west-${i}`, -22, 5 + i * 6, 0.2, 2.5);
	}

	// Short driveway to the warehouse
	road("driveway", 0, 0, 4, 10);

	// Sidewalks (lighter concrete) flanking the main road
	const sidewalkMat = new StandardMaterial("sidewalk", scene);
	sidewalkMat.diffuseColor = new Color3(0.6, 0.6, 0.62);
	const sw = (x: number, z: number, w: number, d: number) => {
		const m = MeshBuilder.CreateBox(`sw-${x}-${z}`, { width: w, depth: d, height: 0.05 }, scene);
		m.position.set(x, 0.025, z);
		m.material = sidewalkMat;
		return m;
	};
	sw(0, -1.2, 120, 0.6);
	sw(0, -6.8, 120, 0.6);
}

function buildShop(scene: Scene, pos: Vector3, adt: AdvancedDynamicTexture): InteractZone {
	const root = new TransformNode("shop", scene);
	root.position.copyFrom(pos);

	const wallMat = new StandardMaterial("shop-wall", scene);
	wallMat.diffuseColor = new Color3(0.35, 0.45, 0.65);
	const roofMat = new StandardMaterial("shop-roof", scene);
	roofMat.diffuseColor = new Color3(0.15, 0.2, 0.3);
	const floorMat = new StandardMaterial("shop-floor", scene);
	floorMat.diffuseColor = new Color3(0.6, 0.6, 0.6);

	const W = 8;
	const D = 8;
	const H = 4;
	const T = 0.2;
	const DOOR_W = 2.2;

	const floor = MeshBuilder.CreateBox("shop-floor", { width: W, depth: D, height: 0.1 }, scene);
	floor.position.y = 0.05;
	floor.material = floorMat;
	floor.parent = root;

	const roof = MeshBuilder.CreateBox("shop-roof", { width: W + 0.3, depth: D + 0.3, height: 0.2 }, scene);
	roof.position.y = H + 0.1;
	roof.material = roofMat;
	roof.parent = root;

	const wall = (name: string, w: number, d: number, x: number, z: number) => {
		const m = MeshBuilder.CreateBox(name, { width: w, depth: d, height: H }, scene);
		m.position.set(x, H / 2, z);
		m.material = wallMat;
		m.parent = root;
		m.checkCollisions = true;
		return m;
	};
	wall("shop-n", W, T, 0, D / 2);
	wall("shop-e", T, D, W / 2, 0);
	wall("shop-w", T, D, -W / 2, 0);
	const seg = (W - DOOR_W) / 2;
	wall("shop-s-l", seg, T, -(DOOR_W / 2 + seg / 2), -D / 2);
	wall("shop-s-r", seg, T, DOOR_W / 2 + seg / 2, -D / 2);

	const signAnchor = MeshBuilder.CreateBox("shop-sign-anchor", { size: 0.1 }, scene);
	signAnchor.isVisible = false;
	signAnchor.parent = root;
	signAnchor.position.set(0, H + 1, -D / 2);

	const sign = makeSign(adt, signAnchor, "🛒 PARTS SHOP", "#fc7");

	return {
		id: "shop",
		displayName: "Parts Shop",
		center: pos.add(new Vector3(0, 1, 0)),
		radius: 5.5,
		root,
		signLabel: sign,
	};
}

function buildKiosk(scene: Scene, pos: Vector3, adt: AdvancedDynamicTexture): InteractZone {
	const root = new TransformNode("kiosk", scene);
	root.position.copyFrom(pos);

	const podiumMat = new StandardMaterial("kiosk-podium", scene);
	podiumMat.diffuseColor = new Color3(0.2, 0.35, 0.25);
	const screenMat = new StandardMaterial("kiosk-screen", scene);
	screenMat.diffuseColor = new Color3(0.05, 0.1, 0.08);
	screenMat.emissiveColor = new Color3(0.1, 0.4, 0.2);

	const base = MeshBuilder.CreateBox("kiosk-base", { width: 1.2, depth: 0.8, height: 1.1 }, scene);
	base.position.y = 0.55;
	base.material = podiumMat;
	base.parent = root;
	base.checkCollisions = true;

	const screen = MeshBuilder.CreateBox("kiosk-screen", { width: 1.1, depth: 0.08, height: 0.7 }, scene);
	screen.position.set(0, 1.55, -0.36);
	screen.rotation.x = -0.3;
	screen.material = screenMat;
	screen.parent = root;

	const signAnchor = MeshBuilder.CreateBox("kiosk-anchor", { size: 0.1 }, scene);
	signAnchor.isVisible = false;
	signAnchor.parent = root;
	signAnchor.position.set(0, 2.4, 0);

	const sign = makeSign(adt, signAnchor, "📋 CONTRACTS", "#2c6");

	return {
		id: "kiosk",
		displayName: "Contracts Kiosk",
		center: pos.add(new Vector3(0, 1, 0)),
		radius: 4,
		root,
		signLabel: sign,
	};
}

// Shared materials so all lamps share two StandardMaterials instead of one
// per lamp (cheaper + avoids per-instance shader bloat).
let _poleMat: StandardMaterial | null = null;
let _bulbMat: StandardMaterial | null = null;

function buildStreetLamp(scene: Scene, pos: Vector3) {
	if (!_poleMat) {
		_poleMat = new StandardMaterial("lamp-pole", scene);
		_poleMat.diffuseColor = new Color3(0.2, 0.2, 0.22);
	}
	if (!_bulbMat) {
		_bulbMat = new StandardMaterial("lamp-bulb", scene);
		_bulbMat.diffuseColor = new Color3(1, 0.95, 0.7);
		_bulbMat.emissiveColor = new Color3(1, 0.85, 0.4);
		_bulbMat.disableLighting = true;
	}

	const pole = MeshBuilder.CreateCylinder(`lamp-pole-${pos.x}-${pos.z}`, { diameter: 0.18, height: 4.2 }, scene);
	pole.position.set(pos.x, 2.1, pos.z);
	pole.material = _poleMat;

	const arm = MeshBuilder.CreateBox(`lamp-arm-${pos.x}-${pos.z}`, { width: 0.08, depth: 0.7, height: 0.08 }, scene);
	arm.position.set(pos.x, 4.2, pos.z + 0.35);
	arm.material = _poleMat;

	const bulb = MeshBuilder.CreateSphere(`lamp-bulb-${pos.x}-${pos.z}`, { diameter: 0.3 }, scene);
	bulb.position.set(pos.x, 4.0, pos.z + 0.7);
	bulb.material = _bulbMat;
	// No PointLight per lamp — Babylon caps at 4 lights per mesh and overflowing
	// breaks shader compilation. The emissive bulb is the visible glow.
}

/** Add a handful of ambient pool lights spaced along the main road. */
function buildAmbientStreetLights(scene: Scene) {
	for (const x of [-30, -10, 10, 30]) {
		const l = new PointLight(`ambient-street-${x}`, new Vector3(x, 4, -4), scene);
		l.intensity = 0.18;
		l.range = 14;
	}
}

function buildParkingSpots(scene: Scene, centerOnRoad: Vector3, count: number) {
	const lineMat = new StandardMaterial(`spot-line-${centerOnRoad.x}-${centerOnRoad.z}`, scene);
	lineMat.diffuseColor = new Color3(0.95, 0.95, 0.95);
	const SPOT_W = 1.8;
	const SPOT_D = 3.2;
	const STRIPE_T = 0.1;
	const startX = centerOnRoad.x - ((count - 1) * SPOT_W) / 2;
	for (let i = 0; i <= count; i++) {
		const x = startX + i * SPOT_W - SPOT_W / 2;
		const stripe = MeshBuilder.CreateBox(`spot-${centerOnRoad.x}-${i}`, { width: STRIPE_T, depth: SPOT_D, height: 0.05 }, scene);
		stripe.position.set(x, 0.03, centerOnRoad.z);
		stripe.material = lineMat;
	}
	// Front line of the spots
	const front = MeshBuilder.CreateBox(`spot-front-${centerOnRoad.x}`, { width: count * SPOT_W, depth: STRIPE_T, height: 0.05 }, scene);
	front.position.set(centerOnRoad.x, 0.03, centerOnRoad.z + SPOT_D / 2);
	front.material = lineMat;
}

let _trunkMat: StandardMaterial | null = null;
let _foliageMat: StandardMaterial | null = null;
function buildTree(scene: Scene, pos: Vector3) {
	if (!_trunkMat) {
		_trunkMat = new StandardMaterial("tree-trunk", scene);
		_trunkMat.diffuseColor = new Color3(0.3, 0.2, 0.12);
	}
	if (!_foliageMat) {
		_foliageMat = new StandardMaterial("tree-foliage", scene);
		_foliageMat.diffuseColor = new Color3(0.18, 0.45, 0.18);
	}
	const root = new TransformNode(`tree-${pos.x}-${pos.z}`, scene);
	root.position.copyFrom(pos);
	const trunk = MeshBuilder.CreateCylinder(`tree-trunk-${pos.x}-${pos.z}`, { diameter: 0.4, height: 2.2 }, scene);
	trunk.position.y = 1.1;
	trunk.material = _trunkMat;
	trunk.parent = root;
	const top = MeshBuilder.CreateSphere(`tree-foliage-${pos.x}-${pos.z}`, { diameter: 2.6, segments: 8 }, scene);
	top.position.y = 3.0;
	top.scaling.y = 1.4;
	top.material = _foliageMat;
	top.parent = root;
}

let _containerMats: StandardMaterial[] | null = null;
function buildLoadingDock(scene: Scene, pos: Vector3) {
	if (!_containerMats) {
		_containerMats = [
			new Color3(0.7, 0.15, 0.15),
			new Color3(0.15, 0.35, 0.6),
			new Color3(0.5, 0.4, 0.1),
		].map((c, i) => {
			const m = new StandardMaterial(`container-${i}`, scene);
			m.diffuseColor = c;
			return m;
		});
	}
	const root = new TransformNode(`dock-${pos.x}-${pos.z}`, scene);
	root.position.copyFrom(pos);

	const concreteMat = new StandardMaterial(`dock-pad-${pos.x}-${pos.z}`, scene);
	concreteMat.diffuseColor = new Color3(0.5, 0.5, 0.5);
	const pad = MeshBuilder.CreateBox(`dock-pad-${pos.x}-${pos.z}`, { width: 5, depth: 3, height: 0.3 }, scene);
	pad.position.y = 0.15;
	pad.material = concreteMat;
	pad.parent = root;

	// Stack 2 shipping containers
	const c1 = MeshBuilder.CreateBox(`dock-c1-${pos.x}-${pos.z}`, { width: 4.2, depth: 2, height: 2.4 }, scene);
	c1.position.y = 1.5;
	c1.material = _containerMats[Math.abs(Math.floor(pos.x + pos.z)) % _containerMats.length]!;
	c1.parent = root;
	const c2 = MeshBuilder.CreateBox(`dock-c2-${pos.x}-${pos.z}`, { width: 4.2, depth: 2, height: 2.4 }, scene);
	c2.position.set(0, 3.9, 0);
	c2.material = _containerMats[Math.abs(Math.floor(pos.x * 3 + pos.z)) % _containerMats.length]!;
	c2.parent = root;
}

function buildFillerWarehouse(scene: Scene, pos: Vector3, W: number, D: number) {
	const root = new TransformNode(`filler-${pos.x}-${pos.z}`, scene);
	root.position.copyFrom(pos);

	const wallMat = new StandardMaterial(`filler-wall-${pos.x}-${pos.z}`, scene);
	wallMat.diffuseColor = new Color3(0.55, 0.55, 0.6);
	const roofMat = new StandardMaterial(`filler-roof-${pos.x}-${pos.z}`, scene);
	roofMat.diffuseColor = new Color3(0.25, 0.25, 0.28);

	const H = 5 + Math.random() * 2;

	const body = MeshBuilder.CreateBox(`filler-${pos.x}-${pos.z}`, { width: W, depth: D, height: H }, scene);
	body.position.y = H / 2;
	body.material = wallMat;
	body.parent = root;
	body.checkCollisions = true;

	const roof = MeshBuilder.CreateBox(`filler-roof-${pos.x}-${pos.z}`, { width: W + 0.3, depth: D + 0.3, height: 0.3 }, scene);
	roof.position.y = H + 0.15;
	roof.material = roofMat;
	roof.parent = root;
}
